import type { FC } from 'react'
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ArcaplanetMapLocationPin } from 'src/components/icons/ArcaplanetMapLocationPin'
import { ArcaplanetSelectedFastMapLocationPin } from 'src/components/icons/ArcaplanetSelectedFastMapLocationPin'
import { useGoogleMapsAPI } from 'src/sdk/googleMaps/useGoogleMapsAPI'
import { formatSvgToDataURL } from 'src/utils/stringUtils'

export type GoogleMapsContext = {
  googleMapInstance: any
  placesAPI: any
  markerAPI: any
  googleAPI: any
  placesServiceAPI: any
  previouslySelectedMarker: React.MutableRefObject<any>
  visibleMapMarkers: React.MutableRefObject<any[]>
  centerMap: (a: MapCenter) => void
  cleanMap: () => void
  handleChangeMarkerPin: (a: any, marker: any) => void
}

const GoogleMapsContext = createContext({} as GoogleMapsContext)

export const GoogleMapsProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const {
    places: placesAPI,
    marker: markerAPI,
    google: googleAPI,
  } = useGoogleMapsAPI()
  const googleMapInstance = useRef<any>(null)
  const previouslySelectedMarker = useRef<any>(null)
  const visibleMapMarkers = useRef<Array<any>>([])
  const [placesServiceAPI, setPlacesServiceAPI] = useState<any>(null)

  useEffect(() => {
    if (googleMapInstance && placesAPI) {
      setPlacesServiceAPI(
        new placesAPI.PlacesService(googleMapInstance.current)
      )
    }
  }, [placesAPI, googleMapInstance])

  const centerMap = ({ lat, lng, zoom }: MapCenter) => {
    googleMapInstance.current?.setCenter({
      lat: lat,
      lng: lng,
    })

    googleMapInstance.current?.setZoom(zoom)
  }

  const cleanMap = () => {
    visibleMapMarkers.current &&
      visibleMapMarkers.current.map((el: any) => el.setMap(null))

    visibleMapMarkers.current = []
    previouslySelectedMarker.current = null
  }

  const handleChangeMarkerPin = (listData: any, marker: any) => {
    if (previouslySelectedMarker.current) {
      previouslySelectedMarker?.current?.setIcon(
        formatSvgToDataURL(
          listData?.find(
            (item: any) => item.id === previouslySelectedMarker.current.id
          ).icon
        )
      )
    }

    listData?.find((item: any) => item.id === marker.id)?.fastTime
      ? marker?.setIcon(
          formatSvgToDataURL(<ArcaplanetSelectedFastMapLocationPin />)
        )
      : marker?.setIcon(
          formatSvgToDataURL(
            <ArcaplanetMapLocationPin width="50" height="52" />
          )
        )

    previouslySelectedMarker.current = marker
  }

  const value = {
    googleAPI,
    placesAPI,
    markerAPI,
    placesServiceAPI,
    googleMapInstance,
    previouslySelectedMarker,
    visibleMapMarkers,
    centerMap,
    cleanMap,
    handleChangeMarkerPin,
  }

  return (
    <GoogleMapsContext.Provider value={value}>
      {children}
    </GoogleMapsContext.Provider>
  )
}

export const useGoogleMapsContext = () => {
  return useContext(GoogleMapsContext)
}
